/**
 * Iter 132 — Calls (MCUBE cloud telephony) page.
 *
 * Two tabs:
 *   1. Logs      — chronological call log with filters, expandable rows
 *                  showing transcript + AI analysis.
 *   2. Analytics — per-agent leaderboard + per-client (entity) rollup for
 *                  the past N days.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PhoneCall, Sparkles, Play, ChevronDown, ChevronRight, RefreshCw, Users, FileText, TrendingUp, Mail, BellOff } from "lucide-react";

const STATUS_COLOR = {
  completed: "bg-emerald-50 text-emerald-700 border-emerald-500/40",
  ringing: "bg-blue-50 text-blue-700 border-blue-500/40",
  initiating: "bg-blue-50 text-blue-700 border-blue-500/40",
  failed: "bg-red-50 text-red-700 border-red-500/40",
  no_answer: "bg-amber-50 text-amber-700 border-amber-500/40",
  missed: "bg-amber-50 text-amber-700 border-amber-500/40",
  busy: "bg-amber-50 text-amber-700 border-amber-500/40",
  cancelled: "bg-foreground/10 text-foreground/60 border-foreground/25",
};

const SENTIMENT_COLOR = {
  positive: "text-emerald-700",
  neutral: "text-foreground/60",
  negative: "text-red-700",
};

const URGENCY_COLOR = {
  high: "bg-red-50 text-red-700 border-red-500/50",
  medium: "bg-amber-50 text-amber-700 border-amber-500/50",
  low: "bg-foreground/10 text-foreground/60 border-foreground/30",
  none: "bg-foreground/5 text-foreground/50 border-foreground/25",
};

function fmtDuration(sec) {
  if (!sec || sec < 0) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
}

export default function Calls() {
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="calls-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <PhoneCall className="w-6 h-6 text-indigo-600" /> Calls
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Every click-to-call, inbound and outbound conversation captured by MCUBE — with automated Whisper transcript and Claude AI analysis.
        </p>
      </div>
      <Tabs defaultValue="logs" className="w-full">
        <TabsList className="rounded-none border-b border-border w-full justify-start bg-transparent p-0">
          <TabsTrigger value="logs" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary" data-testid="calls-tab-logs">
            <FileText className="w-4 h-4 mr-1" /> Logs
          </TabsTrigger>
          <TabsTrigger value="agents" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary" data-testid="calls-tab-agents">
            <TrendingUp className="w-4 h-4 mr-1" /> Agent leaderboard
          </TabsTrigger>
          <TabsTrigger value="clients" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary" data-testid="calls-tab-clients">
            <Users className="w-4 h-4 mr-1" /> Per-client rollup
          </TabsTrigger>
        </TabsList>
        <TabsContent value="logs" className="mt-6"><LogsTab /></TabsContent>
        <TabsContent value="agents" className="mt-6"><AgentsTab /></TabsContent>
        <TabsContent value="clients" className="mt-6"><ClientsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ─────────────── Logs tab ───────────────

function LogsTab() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filters, setFilters] = useState({ status: "", since_days: 30, search: "" });
  const [sendingDigest, setSendingDigest] = useState(false);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.set("status", filters.status);
      if (filters.since_days) params.set("since_days", filters.since_days);
      params.set("limit", "100");
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/calls?${params}`);
      setRows(data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't load calls")); }
    finally { setLoading(false); }
  }, [activeWorkspaceId, filters.status, filters.since_days]);
  useEffect(() => { load(); }, [load]);

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/calls/digest/send-now`);
      toast.success(data.sent
        ? "Call intelligence digest sent to workspace admins."
        : "Digest generated — no data in the last 7 days, nothing emailed.");
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't send digest — you must be owner/admin.")); }
    finally { setSendingDigest(false); }
  };

  const filtered = rows.filter((r) => {
    if (!filters.search.trim()) return true;
    const q = filters.search.trim().toLowerCase();
    return [r.customer_number, r.customer_name, r.entity_name, r.agent_name,
            r.ai_summary, r.transcript].some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <Input placeholder="Search number, name, transcript…" value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="h-9 rounded-none max-w-sm" data-testid="calls-search" />
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="h-9 border border-border px-2 text-sm rounded-none bg-background" data-testid="calls-status-filter">
          <option value="">All statuses</option>
          <option value="completed">Completed</option>
          <option value="ringing">Ringing</option>
          <option value="initiating">Initiating</option>
          <option value="failed">Failed</option>
          <option value="no_answer">No answer</option>
          <option value="busy">Busy</option>
          <option value="missed">Missed</option>
        </select>
        <select value={filters.since_days} onChange={(e) => setFilters({ ...filters, since_days: Number(e.target.value) })}
          className="h-9 border border-border px-2 text-sm rounded-none bg-background" data-testid="calls-since-filter">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
        <Button variant="outline" size="sm" onClick={load} className="rounded-none" data-testid="calls-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={sendDigestNow} disabled={sendingDigest} className="rounded-none" data-testid="calls-send-digest-now"
          title="Preview the Monday-morning Call Intelligence email for the past 7 days.">
          <Mail className="w-3.5 h-3.5 mr-1" /> {sendingDigest ? "Sending…" : "Send digest now"}
        </Button>
        <div className="text-xs text-muted-foreground ml-auto">
          {loading ? "Loading…" : `${filtered.length} of ${rows.length}`}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading calls…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="calls-empty">
          No calls yet.
          {rows.length === 0 && (
            <> Configure MCUBE in <Link to="/app/settings?tab=integrations" className="text-primary underline">Settings → Integrations</Link> and place your first call using the phone icon on any prospect, client or ticket.</>
          )}
        </div>
      ) : (
        <ul className="divide-y divide-border border border-border" data-testid="calls-list">
          {filtered.map((c) => (
            <CallRow key={c.id} call={c} expanded={expandedId === c.id}
              onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
              onRefresh={load} />
          ))}
        </ul>
      )}
    </div>
  );
}

function CallRow({ call, expanded, onToggle, onRefresh }) {
  const { activeWorkspaceId } = useAuth();
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [audioErrored, setAudioErrored] = useState(false);
  const [followupOpen, setFollowupOpen] = useState(false);

  useEffect(() => {
    if (expanded && !detail) {
      api.get(`/workspaces/${activeWorkspaceId}/calls/${call.id}`)
        .then(({ data }) => setDetail(data)).catch(() => setDetail(call));
    }
  }, [expanded, activeWorkspaceId, call, detail]);

  const transcribe = async () => {
    setBusy(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/calls/${call.id}/transcribe`);
      toast.success("Transcription queued — refreshing in ~30s");
      setTimeout(onRefresh, 30000);
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't start transcription")); }
    finally { setBusy(false); }
  };
  const reanalyze = async () => {
    setBusy(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/calls/${call.id}/analyze`);
      toast.success("Analysis re-queued — refreshing in ~15s");
      setTimeout(() => { setDetail(null); onRefresh(); }, 15000);
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't re-analyse")); }
    finally { setBusy(false); }
  };
  const snooze = async () => {
    const reason = window.prompt("Optional — why is this AI suggestion wrong? (audit trail)") || "";
    setBusy(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/calls/${call.id}/followup/snooze`, { reason });
      toast.success("Snoozed — no more auto-follow-ups for this call");
      setDetail(null);
      onRefresh();
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't snooze")); }
    finally { setBusy(false); }
  };
  const draftFollowup = async () => {
    setBusy(true);
    const tid = toast.loading("Claude is drafting a follow-up email…");
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/calls/${call.id}/draft-followup`, {});
      toast.success("Draft ready", { id: tid });
      setDetail({ ...(detail || call), followup_draft: data.draft });
      setFollowupOpen(true);
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't draft follow-up"), { id: tid }); }
    finally { setBusy(false); }
  };

  const cur = detail || call;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  // If MCUBE's URL 404s at play-time, transparently fall back to the
  // object-storage backup URL exposed by the backend.
  const audioSrc = audioErrored && cur.recording_url_backup
    ? `${backendUrl}${cur.recording_url_backup}`
    : cur.recording_url;

  return (
    <li className="p-3 hover:bg-secondary/20" data-testid={`call-row-${call.id}`}>
      <button type="button" onClick={onToggle}
        className="w-full flex items-center gap-3 text-left" data-testid={`call-row-toggle-${call.id}`}>
        {expanded ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">
            {call.customer_name || call.customer_number}
            {call.entity_name && (
              <span className="text-[11px] text-muted-foreground"> · {call.entity_type} · {call.entity_name}</span>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {call.direction === "inbound" ? "◀ inbound" : "▶ outbound"} · agent: {call.agent_name || "—"} · {fmtDuration(call.duration_sec)} · {(call.created_at || "").slice(0, 16).replace("T", " ")}
          </div>
        </div>
        {call.ai_followup_urgency && call.ai_followup_urgency !== "none" && (
          <span className={`text-[10px] font-semibold uppercase border px-1.5 py-0.5 rounded-none ${URGENCY_COLOR[call.ai_followup_urgency] || ""}`}
            data-testid={`call-urgency-${call.id}`}
            title={`Follow-up urgency: ${call.ai_followup_urgency}`}>
            {call.ai_followup_urgency}
          </span>
        )}
        {call.ai_sentiment && (
          <span className={`text-[10px] font-semibold uppercase ${SENTIMENT_COLOR[call.ai_sentiment] || ""}`}>
            {call.ai_sentiment}
          </span>
        )}
        <Badge variant="outline" className={`rounded-none text-[10px] ${STATUS_COLOR[call.status] || ""}`}>
          {call.status}
        </Badge>
      </button>
      {expanded && (
        <div className="mt-3 ml-6 space-y-3 text-xs">
          {cur.failure_reason && (
            <div className="border-l-2 border-destructive/50 pl-3 text-destructive"><strong>Failure:</strong> {cur.failure_reason}</div>
          )}
          {audioSrc ? (
            <div className="border border-border p-3 space-y-2">
              <div className="label-eyebrow text-[10px] flex items-center gap-2">
                Recording
                {audioErrored && cur.recording_url_backup && (
                  <span className="text-[10px] text-amber-700 normal-case tracking-normal">(fallback from Object Storage backup)</span>
                )}
                {cur.recording_backup_at && (
                  <span className="text-[10px] text-emerald-700 normal-case tracking-normal" title={`Backed up ${cur.recording_backup_at}`}>· backed up</span>
                )}
              </div>
              <audio src={audioSrc} controls className="w-full"
                onError={() => setAudioErrored(true)}
                data-testid={`call-audio-${call.id}`} />
            </div>
          ) : (
            <div className="text-muted-foreground italic">No recording available yet.</div>
          )}
          {cur.ai_summary && (
            <div className="border border-primary/30 p-3 bg-primary/5 space-y-2">
              <div className="flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-primary" />
                <div className="label-eyebrow text-[10px] text-primary">AI analysis</div>
                {cur.ai_followup_urgency && cur.ai_followup_urgency !== "none" && (
                  <span className={`text-[10px] font-semibold uppercase border px-1.5 py-0.5 ml-auto ${URGENCY_COLOR[cur.ai_followup_urgency] || ""}`}>
                    urgency: {cur.ai_followup_urgency}
                  </span>
                )}
              </div>
              <div><strong>Summary:</strong> {cur.ai_summary}</div>
              {cur.ai_next_action && <div><strong>Next action:</strong> {cur.ai_next_action}</div>}
              {cur.ai_deal_stage && <div><strong>Deal stage:</strong> {cur.ai_deal_stage}</div>}
              {cur.ai_red_flags && cur.ai_red_flags.length > 0 && (
                <div><strong>Red flags:</strong> {cur.ai_red_flags.join(", ")}</div>
              )}
              {cur.ai_topics && cur.ai_topics.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {cur.ai_topics.map((t) => <span key={t} className="text-[10px] px-1.5 py-0.5 border border-border">{t}</span>)}
                </div>
              )}
              {cur.followup_task_id && !cur.followup_snoozed_at && (
                <div className="text-[11px] text-emerald-700 border-l-2 border-emerald-500/40 pl-2 mt-1">
                  ✓ Auto follow-up task created (assigned to {cur.agent_name || "agent"}).
                </div>
              )}
              {cur.followup_snoozed_at && (
                <div className="text-[11px] text-muted-foreground border-l-2 border-foreground/25 pl-2 mt-1">
                  Follow-up suggestion snoozed{cur.followup_snooze_reason ? ` — "${cur.followup_snooze_reason}"` : ""}.
                </div>
              )}
            </div>
          )}
          {cur.transcript ? (
            <details className="border border-border p-3" data-testid={`call-transcript-${call.id}`}>
              <summary className="cursor-pointer label-eyebrow text-[10px]">Full transcript</summary>
              <div className="whitespace-pre-wrap mt-2 max-h-72 overflow-y-auto">{cur.transcript}</div>
            </details>
          ) : cur.recording_url ? (
            <div className="text-muted-foreground italic">Transcript not generated yet.</div>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {cur.recording_url && !cur.transcript && (
              <Button size="sm" variant="outline" className="rounded-none" onClick={transcribe} disabled={busy} data-testid={`call-transcribe-${call.id}`}>
                <Play className="w-3 h-3 mr-1" /> Transcribe now
              </Button>
            )}
            {cur.transcript && (
              <Button size="sm" variant="outline" className="rounded-none" onClick={reanalyze} disabled={busy} data-testid={`call-reanalyze-${call.id}`}>
                <Sparkles className="w-3 h-3 mr-1" /> Re-run AI analysis
              </Button>
            )}
            {cur.ai_summary && (
              <Button size="sm" className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
                       onClick={draftFollowup} disabled={busy}
                       data-testid={`call-draft-followup-${call.id}`}
                       title="Have Claude draft a warm, specific follow-up email based on this call.">
                <Mail className="w-3 h-3 mr-1" /> {cur.followup_draft ? "Redraft follow-up" : "Draft follow-up"}
              </Button>
            )}
            {cur.followup_draft?.body && (
              <Button size="sm" variant="outline" className="rounded-none"
                       onClick={() => setFollowupOpen(true)}
                       data-testid={`call-open-followup-${call.id}`}>
                <Mail className="w-3 h-3 mr-1" /> Send drafted follow-up
              </Button>
            )}
            {cur.followup_sent_at && (
              <span className="text-[10px] text-emerald-700 self-center" data-testid={`call-followup-sent-${call.id}`}>
                ✓ Sent {cur.followup_last_to_email ? `to ${cur.followup_last_to_email}` : ""} · {cur.followup_sent_at.slice(0, 16).replace("T"," ")}
              </span>
            )}
            {cur.ai_summary && !cur.followup_snoozed_at && (
              <Button size="sm" variant="ghost" className="rounded-none text-muted-foreground ml-auto" onClick={snooze} disabled={busy} data-testid={`call-snooze-${call.id}`}
                title="This AI follow-up suggestion is a false positive — never spawn another for this call.">
                <BellOff className="w-3 h-3 mr-1" /> Snooze suggestion
              </Button>
            )}
          </div>
        </div>
      )}
      {followupOpen && (
        <FollowupDialog
          call={cur}
          onClose={() => setFollowupOpen(false)}
          onSent={() => { setFollowupOpen(false); setDetail(null); onRefresh(); }}
        />
      )}
    </li>
  );
}


// ─────────────── Follow-up email dialog ───────────────

function FollowupDialog({ call, onClose, onSent }) {
  const { activeWorkspaceId } = useAuth();
  const initial = call.followup_draft || {};
  const defaultTo = call.contact_email || call.customer_email || call.lead_email || "";
  const [to, setTo] = useState(defaultTo);
  const [subject, setSubject] = useState(initial.subject || "Following up on our call");
  const [body, setBody] = useState(initial.body || "");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!to.trim()) { toast.error("Recipient email is required"); return; }
    if (!body.trim()) { toast.error("Email body is empty"); return; }
    setSending(true);
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/calls/${call.id}/send-followup`,
        { to_email: to.trim(), subject, body });
      if (data.sent) toast.success(`Sent to ${to}`);
      else toast.warning(`Queued as draft — ${data.error || "email provider not configured"}`);
      onSent?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Send failed")); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4"
         data-testid="followup-dialog"
         onClick={onClose}>
      <div className="bg-card border border-border max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border px-4 py-3 flex items-center gap-2">
          <Mail className="w-4 h-4 text-emerald-600" />
          <div className="font-semibold text-sm">Send follow-up email</div>
          {call.followup_draft?.model && (
            <span className="text-[10px] text-muted-foreground ml-auto">drafted by {call.followup_draft.model}</span>
          )}
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">To</label>
            <Input value={to} onChange={(e) => setTo(e.target.value)}
                    placeholder="recipient@company.com" className="rounded-none"
                    data-testid="followup-to" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                    className="rounded-none" data-testid="followup-subject" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Body</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)}
                       className="w-full min-h-[240px] border border-border bg-background text-sm p-2 font-mono"
                       data-testid="followup-body" />
          </div>
        </div>
        <div className="border-t border-border px-4 py-3 flex items-center gap-2">
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white ml-auto"
                   disabled={sending} onClick={send}
                   data-testid="followup-send">
            <Mail className="w-3 h-3 mr-1" /> {sending ? "Sending…" : "Send"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─────────────── Analytics tabs ───────────────

function AgentsTab() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    api.get(`/workspaces/${activeWorkspaceId}/calls/analytics/agents?since_days=${days}`)
      .then(({ data }) => setRows(data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [activeWorkspaceId, days]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Window:</span>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="h-9 border border-border px-2 text-sm rounded-none bg-background" data-testid="agents-window">
          <option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option>
        </select>
      </div>
      {loading ? <div className="text-sm text-muted-foreground py-6">Loading…</div>
        : rows.length === 0 ? <div className="text-sm text-muted-foreground py-6">No calls in this window.</div>
          : (
            <table className="w-full border border-border" data-testid="agents-table">
              <thead className="bg-secondary/40">
                <tr className="text-[10px] uppercase tracking-widest">
                  <th className="p-2 text-left">Agent</th>
                  <th className="p-2 text-right">Total calls</th>
                  <th className="p-2 text-right">Connected %</th>
                  <th className="p-2 text-right">Avg duration</th>
                  <th className="p-2 text-right">Avg sentiment</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {rows.map((r, i) => (
                  <tr key={r.agent_user_id || i} className="border-t border-border" data-testid={`agent-row-${r.agent_user_id}`}>
                    <td className="p-2">{r.agent_name || "—"}</td>
                    <td className="p-2 text-right font-semibold">{r.total_calls}</td>
                    <td className="p-2 text-right">{r.connected_pct}%</td>
                    <td className="p-2 text-right">{fmtDuration(r.avg_duration_sec)}</td>
                    <td className={`p-2 text-right ${r.avg_sentiment > 0.1 ? "text-emerald-700" : r.avg_sentiment < -0.1 ? "text-red-700" : "text-muted-foreground"}`}>
                      {r.avg_sentiment?.toFixed(2) || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
    </div>
  );
}

function ClientsTab() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    api.get(`/workspaces/${activeWorkspaceId}/calls/analytics/clients?since_days=${days}`)
      .then(({ data }) => setRows(data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [activeWorkspaceId, days]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Window:</span>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="h-9 border border-border px-2 text-sm rounded-none bg-background" data-testid="clients-window">
          <option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option>
        </select>
      </div>
      {loading ? <div className="text-sm text-muted-foreground py-6">Loading…</div>
        : rows.length === 0 ? <div className="text-sm text-muted-foreground py-6">No entity-tagged calls in this window.</div>
          : (
            <table className="w-full border border-border" data-testid="clients-table">
              <thead className="bg-secondary/40">
                <tr className="text-[10px] uppercase tracking-widest">
                  <th className="p-2 text-left">Entity</th>
                  <th className="p-2 text-left">Type</th>
                  <th className="p-2 text-right">Total</th>
                  <th className="p-2 text-right">Connected %</th>
                  <th className="p-2 text-right">Last call</th>
                  <th className="p-2 text-right">Sentiment</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {rows.map((r, i) => (
                  <tr key={`${r.entity_type}-${r.entity_id}-${i}`} className="border-t border-border">
                    <td className="p-2">{r.entity_name || "—"}</td>
                    <td className="p-2 text-muted-foreground">{r.entity_type}</td>
                    <td className="p-2 text-right font-semibold">{r.total_calls}</td>
                    <td className="p-2 text-right">{r.connected_pct}%</td>
                    <td className="p-2 text-right text-[11px]">{(r.last_call_at || "").slice(0, 10)}</td>
                    <td className={`p-2 text-right ${r.avg_sentiment > 0.1 ? "text-emerald-700" : r.avg_sentiment < -0.1 ? "text-red-700" : "text-muted-foreground"}`}>
                      {r.avg_sentiment?.toFixed(2) || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
    </div>
  );
}
